<template>
    <div v-if="dataReady" class="evidence-detail">
      <section class="approval-section">
        <k-panel :hasContentToggle="false" title="Approve Evidence">
          <template #body>
            <p>
              Once all competencies have been met and feedback has been resolved, click 'Approve Evidence' to mark this evidence as complete.
            </p>
            <p>
              Approving evidence locks evidence feedback: you will no longer be able to add text or uploads for the current version in New Feedback.
            </p>
            <div v-if="!isEvidenceApproved" class="button-container">
              <button
                class="btn btn-success"
                :disabled="isEvidenceApproved || approving"
                @click="handleApprovalProcess"
              >
                Approve Evidence
              </button>
            </div>
            <div v-else class="approved-container">
              <k-status-pill type="success" statusValue="approved">
                <template #label>
                  <i class="fas fa-check" aria-hidden="true"></i> Evidence approved
                </template>
              </k-status-pill>
            </div>
          </template>
        </k-panel>
      </section>

      <section class="summary-section">
        <k-panel :hasContentToggle="false" title="Summary">
          <template #body>
            <k-table
              :headers="summaryHeaders"
              :rows="summaryRows"
              :hideable="false"
              :downloadable="false"
              :panel="false"
              :max="1"
            />
          </template>
        </k-panel>
      </section>

      <section class="previous-versions-section">
        <k-panel :hasContentToggle="true" :showContent="showPrev" title="Previous versions" @toggle="showPrev = $event">
          <template #body>
            <div v-if="previousVersions.length === 0">No previous versions</div>
            <k-table
              v-else
              :headers="previousHeaders"
              :rows="previousRows"
              :hideable="false"
              :downloadable="false"
              :panel="false"
              :max="10"
            />
          </template>
        </k-panel>
      </section>

      <section
        class="content-grid"
        :class="{ 'feedback-history-only': isEvidenceApproved }"
      >
        <k-panel
          class="competencies-panel"
          :hasContentToggle="true"
          :showContent="showCompetencies"
          @toggle="showCompetencies = $event"
          title="Competencies"
        >
          <template #body>
            <label class="label-text">Select Competencies relevant to the evidence</label>
            <k-editable-table
              v-if="competenciesTableData.length"
              v-model="competenciesTableData"
              :headers="competencyTableHeaders"
              :max="10"
              :initial-sort-state="initialSortState"
            />
            <div v-else>No competencies available for this project</div>
            <div class="competency-save">
              <button class="btn btn-primary" @click="saveCompetencies" :disabled="savingCompetencies || isEvidenceApproved">Save competencies</button>
            </div>
          </template>
        </k-panel>

        <project-review-feedback
          :project-data="projectData"
          feedback-type="evidence"
          custom-title="All Feedback"
          :read-only="true"
          :evidence-id="evidenceId"
          :versions="versions"
        />

        <k-panel v-if="!isEvidenceApproved" :hasContentToggle="false" title="New Feedback for Current Version">
          <template #body>
            <k-text-editor-with-file-upload
              ref="mentorFeedbackEditor"
              textLabel="Evidence Feedback"
              :displayWarningMsg="hasExistingCurrentFeedback"
              v-model="currentFeedbackText"
              :textMaxLength="4096"
              :acceptedFormats="mentorFileAcceptString"
              :require-text="true"
              dropfield-id="scope-feedback-upload"
              @file-selected="onMentorFileSelect"
              @save="saveCurrentFeedback"
            />
          </template>
        </k-panel>
      </section>
    </div>
    <div v-else class="content-with-spacing"></div>
  </template>

  <style scoped>
  .button-container {
    display: flex;
    justify-content: flex-end;
  }

  .approved-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
  }

  .competency-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .competency-save {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }

  .evidence-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .versions-dropdown :deep(.dropdown-toggle) { white-space: nowrap; }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-top: 20px;
  }

  .content-grid.feedback-history-only {
    grid-template-columns: 1fr;
  }

  .competencies-panel {
    grid-column: 1 / -1;
  }

  .feedback-save {
    display: flex;
    justify-content: flex-end;
  }

  .mentor-dropfield.small :deep(.k-dropbox) {
    min-height: 100px;
  }

  .feedback-warning {
    background: var(--kate-warning);
    color: var(--kate-type-dark);
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 10px;
  }

  /* Use global spinner styles */

  .competency-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .competency-pill {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 13px;
    background-color: var(--kate-type-alt);
    border: 2px solid var(--kate-type-alt);
  }

  .competency-pill.approved {
    background-color: var(--kate-success);
    border-color: var(--kate-success);
  }
  </style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import KEditableTable from '@base-components/k-editable-table.vue';
import KTextEditorWithFileUpload from '@base-components/k-text-editor-with-file-upload.vue';
import KStatusPill from '@base-components/k-status-pill.vue';
import { competencySortBy } from '@utils/sort-table-competencies';
import ProjectReviewFeedback from './project-review-feedback.vue';

export default {
  name: 'ProjectReviewEvidenceDetail',
  components: {
    KPanel,
    KTable,
    ProjectReviewFeedback,
    KEditableTable,
    KTextEditorWithFileUpload,
    KStatusPill,
  },
  props: {
    projectData: { type: Object, required: true },
    evidenceId: { type: Number, required: true },
  },
  data() {
    return {
      dataReady: false,
      evidence: null,
      versions: [],
      showVersionsModal: false,
      showPrev: false,
      competenciesReady: false,
      competenciesTableData: [],
      savingCompetencies: false,
      showCompetencies: true,
      currentFeedbackText: '',
      mentorFile: null,
      approving: false,
    };
  },
  computed: {
    mentorFileAcceptString() {
      const formats = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-powerpoint',
        'image/*',
      ];
      return formats.join(',');
    },
    isEvidenceApproved() {
      const v = this.currentVersion;
      return Boolean(v?.approved || this.evidence?.approved);
    },
    summaryHeaders() {
      return {
        name: { name: 'Name', type: 'string' },
        description: { name: 'Description', type: 'string' },
        version: { name: 'Version', type: 'string' },
        evidence: {
          name: 'Evidence',
          type: 'button',
          options: {
            preset: row => (row.evidence?.signedUrl ? 'download-file' : 'download-link'),
            downloadUrl: row => row.evidence?.signedUrl || row.evidence?.link || '',
            text: 'Download',
            title: row => {
              if (row.evidence?.signedUrl) {
                return 'Download latest';
              }
              if (row.evidence?.link) {
                return `Open link: ${row.evidence.link}`;
              }
              return '';
            },
            ariaLabel: row => (row.evidence?.signedUrl ? 'Download latest' : 'Open evidence link'),
            disabled: row => !row.evidence,
          },
        },
        mentor: {
          name: 'Mentor Comments',
          type: 'button',
          options: {
            preset: 'download-file',
            text: row => (row.mentor?.signedUrl ? 'Download' : 'No mentor comments'),
            title: row => (row.mentor?.signedUrl ? 'Download mentor commented' : 'No mentor comments'),
            ariaLabel: row => (row.mentor?.signedUrl ? 'Download mentor commented' : 'No mentor comments'),
            disabled: row => !row.mentor?.signedUrl,
            downloadUrl: row => row.mentor?.signedUrl || '',
          },
        },
      };
    },
    summaryRows() {
      const e = this.evidence || {};
      const versionOnly = `v${e.version_number || '-'}`;

      let evidenceCell = null;
      if (e.evidence_type && e.evidence_type !== 'link') {
        if (e.signed_url) {
          evidenceCell = {
            type: 'file',
            signedUrl: e.signed_url,
            filename: e.filename || this.filenameFromLink(e.evidence_link),
          };
        }
      } else if (e.evidence_link) {
        evidenceCell = {
          type: 'link',
          link: e.evidence_link,
          filename: this.filenameFromLink(e.evidence_link),
        };
      }

      const mentorCell = (e.mentor_commented_document_signed_url && (e.mentor_commented_document_filename || e.mentor_commented_document_link))
        ? {
          filename: e.mentor_commented_document_filename || this.filenameFromLink(e.mentor_commented_document_link),
          signedUrl: e.mentor_commented_document_signed_url,
        }
        : null;

      return [
        {
          id: e.project_evidence_id || this.evidenceId,
          name: e.name || '-',
          description: e.description || '-',
          version: versionOnly,
          evidence: evidenceCell,
          mentor: mentorCell,
        },
      ];
    },
    currentVersion() {
      return this.versions.find(v => v.is_current_version) || null;
    },
    hasExistingCurrentFeedback() {
      const text = this.currentVersion?.feedback_text;
      return Boolean(text && String(text).trim());
    },
    previousVersions() {
      return this.versions.filter(v => !v.is_current_version);
    },
    currentFilename() {
      return this.filenameFromLink(this.currentVersion?.evidence_link);
    },

    initialSortState() {
      return { headerKey: 'competency_name', ascending: true };
    },

    previousHeaders() {
      return {
        version: {
          name: 'Version',
          type: 'string',
          filterable: false,
          sortable: true,
        },
        download: {
          name: 'Download',
          type: 'button',
          options: {
            preset: 'download-file',
            text: 'Download',
            title: 'Download version',
            ariaLabel: 'Download version',
            callback: this.handlePreviousAction,
          },
        },
        mentor: {
          name: 'Mentor Comments',
          type: 'button',
          options: {
            preset: 'download-file',
            text: row => (row.mentor?.filename ? 'Download' : 'No mentor comments'),
            title: row => (row.mentor?.filename ? 'Download mentor commented' : 'No mentor comments'),
            ariaLabel: row => (row.mentor?.filename ? 'Download mentor commented' : 'No mentor comments'),
            disabled: row => !row.mentor?.filename,
            callback: this.handlePreviousAction,
          },
        },
      };
    },
    previousRows() {
      return this.previousVersions.map(v => ({
        version: `v${v.version_number}`,
        raw: v,
        download: true,
        mentor: v.mentor_commented_document_link ? { filename: this.filenameFromLink(v.mentor_commented_document_link) } : null,
      }));
    },
    competencyTableHeaders() {
      return [
        {
          key: 'checkbox',
          title: '',
          editable: true,
          type: 'boolean',
          required: true,
          sortable: false,
        },
        {
          key: 'competency_name',
          title: 'Competency',
          type: 'string',
          filterable: true,
          sortable: true,
          sortBy: competencySortBy,
        },
        {
          key: 'category',
          title: 'Category',
          type: 'string',
          hasDropdown: true,
        },
        {
          key: 'competency_description',
          title: 'Description',
          type: 'string',
          filterable: true,
        },
      ];
    },
  },
  mounted() {
    this.fetchAll();
  },
  methods: {
    resetKEditor() {
      if (this.$refs.mentorFeedbackEditor) {
        this.$refs.mentorFeedbackEditor.reset();
      }
    },
    emitCrumb() {
      if (this.evidence && this.evidence.name) {
        this.$emit('set-evidence-crumb', this.evidence.name);
      }
    },
    fetchAll() {
      this.dataReady = false;
      this.$Loading.start();
      // Fetch evidence details (dashboard list and filter by id)
      const evidenceReq = this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence`)
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : [];
          const item = list.find(e => e.project_evidence_id === this.evidenceId);
          if (item) {
            this.evidence = item;
            this.emitCrumb();
          } else {
            this.$ktoast.warning('Evidence not found');
          }
        });

      // Fetch all versions
      const versionsReq = this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}/versions`)
        .then(res => {
          this.versions = Array.isArray(res.data) ? res.data : [];
        });

      // Fetch project competencies for selection (after evidence is loaded so we can preselect mapped ones)
      const compsReq = evidenceReq.then(() => this.$http
        .get(`/api/curriculum/programmes/projects/${this.projectData.id}/competencies`)
        .then(res => {
          const all = Array.isArray(res.data) ? res.data : [];
          const selectedIds = new Set((this.evidence?.competency_mappings || []).map(c => c.project_competency_mapping_id));
          this.competenciesTableData = all.map(c => ({
            programme_competency_id: c.id,
            competency_name: c.label,
            category: c.category,
            competency_description: c.description,
            checkbox: selectedIds.has(c.id),
          }));
          this.competenciesReady = true;
        })
        .catch(() => {
          this.competenciesTableData = [];
          this.competenciesReady = true;
        }));

      Promise.allSettled([evidenceReq, versionsReq, compsReq])
        .finally(() => {
          this.dataReady = true;
          this.$Loading.finish();
        });
    },

    filenameFromLink(link) {
      if (!link) return '';
      const parts = link.split('/');
      return parts[parts.length - 1] || link;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      try { return new Date(dateStr).toLocaleString(); } catch (e) { return dateStr; }
    },

    downloadVersion(version) {
      if (!version) return;
      if (version.evidence_type === 'link') {
        if (!version.evidence_link) return;
        window.open(version.evidence_link, '_blank');
        return;
      }
      const filename = this.filenameFromLink(version.evidence_link);
      const url = `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}/v${version.version_number}/${filename}`;
      this.$http.get(url)
        .then(res => {
          const signed = res.data?.document_url;
          if (signed) window.open(signed, '_blank');
          else this.$ktoast.warning('Unable to get download link');
        })
        .catch(err => {
          this.$logger.autowarn('Error downloading evidence version', { projectId: this.projectData.id, evidenceId: this.evidenceId, version: version.version_number }, err);
          this.$ktoast.warning('Error downloading version');
        });
    },

    handlePreviousAction(cellValue, index, row, columnKey) {
      const version = row?.raw;
      if (!version) return;
      if (columnKey === 'download') {
        this.downloadVersion(version);
      } else if (columnKey === 'mentor' && version.mentor_commented_document_link) {
        const filename = this.filenameFromLink(version.mentor_commented_document_link);
        const url = `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}/v${version.version_number}/${filename}`;
        this.$http.get(url)
          .then(res => {
            const signed = res.data?.document_url;
            if (signed) window.open(signed, '_blank');
            else this.$ktoast.warning('Unable to get download link');
          })
          .catch(err => {
            this.$logger.autowarn('Error downloading mentor commented', { projectId: this.projectData.id, evidenceId: this.evidenceId, version: version.version_number }, err);
            this.$ktoast.warning('Error downloading mentor commented');
          });
      }
    },
    async saveCompetencies() {
      this.savingCompetencies = true;
      try {
        const selectedMappingIds = this.competenciesTableData
          .filter(row => row.checkbox)
          .map(row => row.programme_competency_id);

        const payload = {
          programme_id: this.projectData.programme_id,
          name: this.evidence?.name || '',
          description: this.evidence?.description ?? null,
          approved: Boolean(this.isEvidenceApproved),
          project_competency_mapping_ids: selectedMappingIds,
        };

        this.$logger.info('Updating evidence competency mappings', {
          projectId: this.projectData.id,
          evidenceId: this.evidenceId,
          count: selectedMappingIds.length,
        });

        const res = await this.$http.put(
          `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}`,
          payload,
        );

        this.evidence = res.data || this.evidence;

        this.$ktoast.success('Competencies saved');
      } catch (err) {
        this.$logger.autowarn('Error saving evidence competencies', { projectId: this.projectData.id, evidenceId: this.evidenceId }, err);
        this.$ktoast.warning('Error saving competencies');
      } finally {
        this.savingCompetencies = false;
      }
    },
    onMentorFileSelect(file) {
      this.mentorFile = file[0] || null;
    },
    async saveCurrentFeedback() {
      if (this.isEvidenceApproved) {
        this.$ktoast.warning('Evidence is already approved');
        return;
      }
      try {
        this.$logger.info('Saving current version feedback', {
          projectId: this.projectData.id,
          evidenceId: this.evidenceId,
          hasFile: Boolean(this.mentorFile),
        });

        let mentorPath = null;
        if (this.mentorFile) {
          const uploadUrlPayload = {
            file_name: this.mentorFile.name,
            file_size: this.mentorFile.size,
            content_type: this.mentorFile.type,
          };
          const uploadRes = await this.$http.post(
            `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}/mentor-upload-url`,
            uploadUrlPayload,
          );
          const { upload_url: uploadUrl, file_path: filePath } = uploadRes.data || {};
          if (!uploadUrl || !filePath) throw new Error('Missing mentor upload URL');
          await this.$http.put(uploadUrl, this.mentorFile, { headers: { 'Content-Type': this.mentorFile.type || 'application/octet-stream' } });
          mentorPath = filePath;
        }

        const payload = {
          programme_id: this.projectData.programme_id,
          feedback_text: this.currentFeedbackText,
          mentor_commented_document_link: mentorPath,
        };
        const res = await this.$http.put(
          `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}/feedback`,
          payload,
        );

        // Update local state if backend returns updated feedback/evidence
        if (res && res.data) {
          // Some endpoints return evidence; others return feedback object. We keep text state consistent.
          this.currentFeedbackText = '';
          this.mentorFile = null;
          this.resetKEditor();
          // Refresh versions so Previous Evidence Feedback reflects the latest text
          try {
            const vRes = await this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}/versions`);
            this.versions = Array.isArray(vRes.data) ? vRes.data : this.versions;
          } catch (verr) {
            this.$logger.autowarn('Error refreshing versions after feedback save', { projectId: this.projectData.id, evidenceId: this.evidenceId }, verr);
          }
          // Also refresh evidence itself so mentor commented filename and any other fields update
          try {
            const eRes = await this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence`);
            const list = Array.isArray(eRes.data) ? eRes.data : [];
            const item = list.find(e => e.project_evidence_id === this.evidenceId);
            if (item) this.evidence = item;
          } catch (eerr) {
            this.$logger.autowarn('Error refreshing evidence after feedback save', { projectId: this.projectData.id, evidenceId: this.evidenceId }, eerr);
          }
        }
        this.$ktoast.success('Feedback saved');
      } catch (err) {
        this.$logger.autowarn('Error saving feedback', { projectId: this.projectData.id, evidenceId: this.evidenceId }, err);
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error saving feedback';
        this.$ktoast.warning(msg);
      }
    },

    handleApprovalProcess() {
      const msg = 'Please confirm you have reviewed and approved all the competencies, and want to mark the evidence as approved. After approval, evidence feedback will be locked and you will not be able to add further text or uploads for this version.';
      this.$ktoast.confirmToast(msg, 'warning', () => this.approveEvidence(), undefined);
    },

    async approveEvidence() {
      if (this.isEvidenceApproved || this.approving) return;
      this.approving = true;
      try {
        const selectedMappingIds = this.competenciesTableData
          .filter(row => row.checkbox)
          .map(row => row.programme_competency_id);

        const payload = {
          programme_id: this.projectData.programme_id,
          name: this.evidence?.name || '',
          description: this.evidence?.description ?? null,
          approved: true,
          project_competency_mapping_ids: selectedMappingIds,
        };

        this.$logger.info('Approving evidence', {
          projectId: this.projectData.id,
          evidenceId: this.evidenceId,
        });

        const res = await this.$http.put(
          `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}`,
          payload,
        );

        if (res && res.data) {
          this.evidence = res.data;
        }

        // Refresh versions so the approved status on current version updates
        try {
          const vRes = await this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${this.evidenceId}/versions`);
          this.versions = Array.isArray(vRes.data) ? vRes.data : this.versions;
        } catch (verr) {
          this.$logger.autowarn('Error refreshing versions after approval', { projectId: this.projectData.id, evidenceId: this.evidenceId }, verr);
        }

        this.$ktoast.success('Evidence approved');
      } catch (err) {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error approving evidence';
        this.$logger.autowarn('Error approving evidence', { projectId: this.projectData.id, evidenceId: this.evidenceId }, err);
        this.$ktoast.warning(msg);
      } finally {
        this.approving = false;
      }
    },
  },
};
</script>
